import React, { useState, useEffect, useRef, useCallback } from 'react';
import { makeStyles } from '@material-ui/core'
import HMapLabel from './HMapLabel'
import SearchIcon from '@mui/icons-material/Search'
import ArrowDropUPIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

/**
 * Sample Code 
 
const [fields, setFields] = useState({
  auto1: { text: undefined, value: undefined, index: undefined }, <== ** Default value **
})

const setFieldValue = (name, value) => {
  setFields({
      ...fields,
      [`${name}`]: value
  })
}

 <HMapAutoComplete
    width={'30%'}
    selectedOption={fields.auto1}
    setSelectedOption={setFieldValue}
    name={'auto1'} <== ** name equals to the one on the field mentioned above **
    options={[
        {value: 'value 0', text: 'text 0', index: 0},
        {value: 'value 1', text: 'text 1', index: 1},
        {value: 'value 2', text: 'text 2', index: 2},
        {value: 'value 3', text: 'text 3', index: 3},
        {value: 'value 4', text: 'text 4', index: 4},
        {value: 'value 5', text: 'text 5', index: 5},
    ]}
    readonly={true}
/>

 */
const HMapAutoComplete = ({
  width,
  options,
  name,
  selectedOption,
  setSelectedOption,
  readonly,
  onChange
}) => {
  const classes = useStyles({ width })

  const autocompleteRef = useRef(null);

  const [settings, setSettings] = useState({
    selectedItem: { text: undefined, value: undefined, index: undefined },
    selectedIndex: 0,
    filteredOptions: (options && [...options]) || [],
    open: false,
    inputValue: ''
  })

  useEffect(() => {
    const selectedItem =
      selectedOption &&
      options.filter(
        (item) => item.text.toLowerCase() === selectedOption.text.toLowerCase()
      )

    if (selectedItem && selectedItem.length) {
      setSettings({
        ...settings,
        selectedItem: { ...selectedItem },
        inputValue: selectedOption.text,
        filteredOptions: [...options]
      })
    }

    // XXX Fix this in the future
    // eslint-disable-next-line
  }, [])

  const handleClickOutside = useCallback((event) => {
    if (
      autocompleteRef.current &&
      !autocompleteRef.current.contains(event.target)
    ) {
      if (settings.open) {
        setSettings({ ...settings, open: false });
      }
    }
  }, [settings]);

  const handleChange = (e) => {
    const val = e.target.value
    
    const res = !!val
      ? options.filter((item) =>
          item.text.toLowerCase().includes(val.toLowerCase())
        )
      : [...options]
    setSettings({
      ...settings,
      filteredOptions: res,
      open: !!val,
      inputValue: val
    })
  }

  const handleOnClick = (e) => {
    const idx = e.target.getAttribute('data-index')
    // console.log(idx)
    setSettings({
      ...settings,
      filteredOptions: [...options],
      selectedIndex: idx,
      selectedItem: { ...options[idx] },
      open: false,
      inputValue: options[idx].text
    })

    !!setSelectedOption && setSelectedOption(name, { ...options[idx] })

    !!onChange && onChange({target: { ...options[idx], name }})
  }

  const handleOnKeyDown = (e) => {
    if (e.keyCode === 13) {
      const val = e.target.value
      const theItem = settings.filteredOptions[0]

      if (!!val && theItem) {
        setSettings({
          ...settings,
          filteredOptions: [...options],
          selectedIndex: theItem.index,
          selectedItem: { ...theItem },
          open: false,
          inputValue: theItem.text
        })

        !!setSelectedOption && setSelectedOption(name, { ...theItem })

        !!onChange && onChange({target: { ...theItem, name }})
      } else {
        setSettings({
          ...settings,
          open: false,
          inputValue: '',
          filteredOptions: [...options]
        })

        !!setSelectedOption &&
          setSelectedOption(name, {
            ...{ text: undefined, value: undefined, index: undefined }
          })

        !!onChange &&
          onChange({
            target: {
              text: undefined,
              value: undefined,
              index: undefined,
              name
            }
          })
      }
    }
  }

  const handleBlur = (e) => {
    const val = e.target.value

    if (!val) {
      setSettings({
        ...settings,
        open: false,
        inputValue: '',
        filteredOptions: [...options]
      })

      !!setSelectedOption &&
        setSelectedOption(name, { ...{ text: undefined, value: undefined, index: undefined } })
      
      !!onChange &&
        onChange({
          target: {
            text: undefined,
            value: undefined,
            index: undefined,
            name
          }
        })
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside, settings.open]);

  return (
    <div ref={autocompleteRef} name={`${name}AutoComplete`} className={classes.root}>
      {!!readonly ? (
        <HMapLabel
          width={'100%'}
          text={selectedOption && selectedOption.text}
          fontType={'p'}
        />
      ) : (
        <>
          <div className={classes.selectWrapper}>
            <SearchIcon className={classes.iconButton} />
            <input
              type="text"
              name={`${name}SearchBox`}
              className={classes.serachBox}
              onChange={handleChange}
              value={settings.inputValue}
              onKeyDown={handleOnKeyDown}
              onBlur={handleBlur}
            />
            {settings.open ? (
              <ArrowDropUPIcon
                className={classes.iconButton}
                onClick={() => {
                  setSettings({
                    ...settings,
                    open: false
                  })
                }}
              />
            ) : (
              <ArrowDropDownIcon
                className={classes.iconButton}
                onClick={() => {
                  setSettings({
                    ...settings,
                    open: true
                  })
                }}
              />
            )}
          </div>
          <div className={classes.optionsWrapper}>
            {settings.open &&
              settings.filteredOptions.map((item) => {
                return (
                  <div
                    key={`${item.value || item.index}_${item.index}`}
                    name={`${name}Option${item.index}`}
                    data-index={item.index}
                    value={`${item.value || item.index}`}
                    className={classes.option}
                    onClick={handleOnClick}
                  >
                    {item.text}
                  </div>
                )
              })}
          </div>
        </>
      )}
    </div>
  )
}

export default React.memo(HMapAutoComplete)

const useStyles = makeStyles((theme) => ({
  root: {
    width: (props) => (props.width ? props.width : '100%'),
    height: '25px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    boxSizing: 'border-box',
    position: 'relative',
    // padding: '0 10px'
  },
  selectWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    boxSizing: 'border-box',
    border: `1px solid ${theme.palette.tertiary.accent}`,
    color: theme.palette.primary.main,
    borderRadius: '5px',
    position: 'relative',
    '&:hover': {
      cursor: 'pointer'
    }
  },
  iconButton: {
    width: '25px',
    height: '25px',
    boxSizing: 'border-box',
    padding: '2px'
  },
  serachBox: {
    width: '100%',
    height: '100%',
    border: 'none',
    padding: '0 5px',
    color: theme.palette.primary.main,
    ...theme.typography.p,
    '&:focus': {
      outline: 'none'
    }
  },
  optionsWrapper: {
    width: '100%',
    position: 'absolute',
    top: 25,
    left: 0,
    zIndex: 30,
    backgroundColor: 'white',
    color: theme.palette.primary.main,
    borderRadius: '5px',
    boxShadow: '1px 2px 5px 0px rgba(0, 0, 0, 0.25)',
    '&> div:last-child': {
      border: 'none'
    }
  },
  option: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '5px 10px',
    borderBottom: `1px solid ${theme.palette.secondary.main}`,
    ...theme.typography.p,
    color: theme.palette.primary.main,
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: '#f9f9f9'
    }
  },
  selected: {
    backgroundColor: '#f9f9f9'
  }
}))
